import { Phone, MapPin, Camera, PaintRoller, Paintbrush, Palette } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: Camera,
    title: 'Before-and-afters sell the finish',
    text: 'Decorating is the most visual trade there is. A proper gallery of transformations — the tired lounge made sharp, the hallway brought back to life — convinces customers better than any sales pitch.',
  },
  {
    icon: MapPin,
    title: 'You show up for “painter and decorator near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local decorator on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Homeowners can reach you in seconds',
    text: 'Click-to-call, WhatsApp and a simple enquiry form on every page — so someone planning to redecorate before Christmas books a quote instead of scrolling on.',
  },
];

const WORK_TYPES = [
  {
    icon: PaintRoller,
    title: 'Interior transformations',
    text: 'Whole-room and whole-house repaints where the finish is everything. Crisp photos of clean lines and fresh walls do the selling while you’re still on the job.',
  },
  {
    icon: Paintbrush,
    title: 'Wallpapering and feature walls',
    text: 'Detailed, higher-skill work customers actively search for. A dedicated services section shows you do more than magnolia.',
  },
  {
    icon: Palette,
    title: 'Exterior painting',
    text: 'Weatherboard, render, masonry and woodwork — seasonal, higher-value jobs where being visible at the right moment fills your summer diary.',
  },
];

const DECORATOR_FAQS = [
  {
    q: 'My photos are just phone snaps of finished rooms. Is that enough?',
    a: 'Absolutely — that’s exactly what works. Customers want to see real rooms you’ve actually decorated, not staged showroom shots. Take a quick wide photo before you start and another when you’ve finished, and we’ll turn them into a gallery that sells your finish. Phone photos are genuinely fine.',
  },
  {
    q: 'I’m booked through recommendations already. Why bother online?',
    a: 'Because recommendations get checked online before they call. When a neighbour passes your name over the fence, the first thing that person does is look you up. A professional site with your before-and-afters turns that glance into a booking — and catches the new families moving into your area who don’t know anyone yet.',
  },
  {
    q: 'Can the site show interior and exterior work separately?',
    a: 'Yes. Your services are laid out so each customer finds what applies to them — interior repaints and wallpapering on one hand, exterior masonry and woodwork on the other. The Built to Grow package gives you up to ten pages, plenty of room for both.',
  },
  {
    q: 'I’m up a ladder all day. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, your photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function DecoratorsPage() {
  return (
    <>
      <Seo
        title="Websites for Painters & Decorators — Show Off Your Finish | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK painters and decorators. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-painters-decorators"
        image={IMAGES.decoratorHero.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="decorators-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for painters &amp; decorators
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              Your finish is flawless. <span className="text-lime">Your online presence should be too.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a decorator needs to be found and booked locally: a fast,
              mobile-friendly website with a before-and-after gallery, a complete Google Business Profile and
              branded social pages — for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="decorators-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="decorators-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.decoratorHero.url}
              alt={IMAGES.decoratorHero.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a decorator's online presence needs */}
      <section className="bg-offwhite" data-testid="decorators-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how decorating work is won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a decorator’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="decorators-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  The jobs that fill a diary
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to show the full range of your work
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  Customers don’t always know what a decorator covers. Your website spells it out — and shows the
                  quality of each kind of job with real photos of real rooms.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.decoratorDetail.url}
                  alt={IMAGES.decoratorDetail.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="decorators-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a decorating business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Most decorators start with Look Professional: a proper website with a gallery, plus the full
              Google and social setup.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Decorator FAQs */}
      <section className="bg-offwhite" data-testid="decorators-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions decorators ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy decorators
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={DECORATOR_FAQS} testid="decorators-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="decorators-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to be the decorator whose work <span className="text-lime">speaks for itself?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your decorating business and the areas you cover — we’ll recommend the right starting
                point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="decorators-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
